import React, { useImperativeHandle, forwardRef, useState, useEffect } from 'react';
import {
  Grid, Button, Box, Typography, FormControl, FormHelperText, Checkbox, FormControlLabel, CircularProgress,Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import moment from 'moment';
import 'moment/locale/pt-br';

import { transformaTextoCamelCase, resizeImage } from '../util';

moment.locale('pt-br');

const FormularioDocumentos = forwardRef((props, ref) => {
  const [campos, setCampos] = useState([
    { id: 2, nome: 'Identidade' },
    { id: 3, nome: 'Comprovante de Residência' },
    { id: 4, nome: 'Foto do Rosto' }
  ]);
  const extensoessAceitas = ['PNG', 'JPEG', 'JPG', 'DOC', 'DOCX', 'PDF'];
  const tamanhoMaximoArquivo = 2 * 1000 * 1024; // 2MB (ajuste conforme necessário)
  const [dialogDocAviso, setDialogDocAviso] = useState(true);

  const [formData, setFormData] = useState(props.formData);
  const [validacao, setValidacao] = useState([]);
  const [checks, setChecks] = useState({
    declaracaoUm: false,
    declaracaoDois: false,
    declaracaoTres: false
  });
  const [loadingMap, setLoadingMap] = useState({});

  const refNacional = "nacional";

  useEffect(() => {
    atualizaValidacao();
  }, [campos]);

  useEffect(() => {
    props.onChildDataChange(formData);
    validaForm();
  }, [formData]);

  useEffect(() => {
    const novosCampos = {
      outorgado: [
        { id: 20, nome: 'CPF do Outorgado' },
        { id: 21, nome: 'Identidade do Outorgado' },
        { id: 23, nome: 'Certidão Óbito' }
      ],
      menorQuinze: [
        { id: 17, nome: 'CPF Representante Legal' },
        { id: 18, nome: 'Identidade Representante Legal' }
      ],
    };

    setCampos(
      [
        ...campos,
        ...(props.outorgado ? novosCampos.outorgado : []),
        ...(props.menorQuinze ? novosCampos.menorQuinze : []),
        ...(props.nacionalidade === refNacional ? [{ id: 1, nome: 'CPF', obrigatorio: false }] : [])
      ]);

    if (props.formData) {
      validaForm();
    }
    else {
      atualizaValidacao();
    }
  }, []);

  const atualizaValidacao = () => {
    campos.forEach(campo => {
      if (campo.obrigatorio !== false)
        setValidacao((prev) => ({
          ...prev,
          [objNome(campo.nome)]: false,
        }));
    });
  }

  const validaForm = () => {
    const propriedades = Object.keys(validacao);
    const novaValidacao = { ...validacao };

    propriedades.forEach(propriedade => {
      if (formData) {
        novaValidacao[propriedade] = (formData && formData[propriedade])
          ? validaArquivo(formData[propriedade].obj[0])
          : false;
      }
    });
    setValidacao(novaValidacao);
  }

  const validaArquivo = (file) => {
    try {
      if (!file) return false;
      const nome = file.name;
      const size = file.size;
      if (!file.size || !file.name) return false;
      const partes = nome.split('.');
      const extensao = partes[partes.length - 1];
      return extensoessAceitas.some(t => t.toLowerCase() === extensao.toLowerCase()) && tamanhoMaximoArquivo >= size;
    } catch (error) {
      console.error(error, 'Erro validação arquivo');
      return false;
    }
  }

  const handleFileChange = async (event, id) => {
    const { name, files } = event.target;

    if (files && files[0]) {
      let file = files[0];

      if (file.size > tamanhoMaximoArquivo && file.type.startsWith('image/')) {
        setLoadingMap(prev => ({ ...prev, [name]: true }));
        try {
          file = await resizeImage(file, tamanhoMaximoArquivo);
        } catch (error) {
          console.error("Erro ao redimensionar imagem", error);
        } finally {
          setLoadingMap(prev => ({ ...prev, [name]: false }));
        }
      }
      else if (file.size > tamanhoMaximoArquivo) {
        alert(`O documento anexado é maior que o permitido, ${(tamanhoMaximoArquivo / 1024) / 1000}MB`);
      }

      setFormData((prevFiles) => ({
        ...prevFiles,
        [name]: { obj: [file], id: id, nmOriginal: file.name },
      }));
    }
  };

  const handleCheck = (event) => {
    const { name, value, type, checked } = event.target;
    setChecks({
      ...checks,
      [name]: type === 'checkbox' ? checked : value,
    });
  }

  const objNome = (nome) => {
    return transformaTextoCamelCase(nome);
  }

  useImperativeHandle(ref, () => ({
    validarFormulario() {
      if (Object.values(validacao).some(value => !value) || Object.values(checks).some(value => !value)) {
        return false;
      }
      return true;
    },
    getDados() {
      return formData;
    }
  }));

  return (
    <>
      <Box sx={{ flexGrow: 1, p: 2 }}>
        <form>
          <Grid container spacing={2} sx={{ display: 'flex', flexWrap: 'wrap' }}>
            {campos.map((label) => {
              const fieldName = objNome(label.nome);
              return (
                <Grid key={label.id} item xs={12} sm={6} md={3}>
                  <FormControl fullWidth
                    required
                    error={!formData?.impressao && !validacao[fieldName] && label.obrigatorio !== false}>
                    <Button
                      variant="contained"
                      component="label"
                      fullWidth
                      sx={{ mt: 2, position: 'relative' }}
                      style={{ textAlign: 'center' }}
                      disabled={loadingMap[fieldName] === true}
                    >
                      {loadingMap[fieldName] ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        label.nome
                      )}
                      <input
                        type="file"
                        name={fieldName}
                        hidden
                        accept={extensoessAceitas.map(t => `.${t.toLowerCase()},`)}
                        onChange={(e) => handleFileChange(e, label.id)}
                      />
                    </Button>
                    <FormHelperText>
                      {(formData && formData[fieldName]?.nmOriginal)
                        ? formData[fieldName]?.nmOriginal
                        : (formData && formData[fieldName]
                          ? formData[fieldName].obj[0].name
                          : 'Nenhum arquivo escolhido')}
                    </FormHelperText>
                  </FormControl>
                </Grid>
              );
            })}

            <Grid item xs={12}>
              <Box mt={2}>
                <Typography variant="body2" color="textSecondary">
                  Tamanho máximo do arquivo: {(tamanhoMaximoArquivo / 1024) / 1000}MB. Formatos aceitos: {extensoessAceitas.join(', ')}.
                </Typography>
              </Box>
            </Grid>

            <Grid item>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData?.impressao || checks?.declaracaoUm || props.finalizado}
                    onChange={handleCheck}
                    name="declaracaoUm"
                  />
                }
                label={<span style={{ color: !formData?.impressao && !checks.declaracaoUm && !props.finalizado ? '#d32f2f' : '' }}>
                  Na forma dos Arts. 97 e 98 da Lei nº 9.610/98, venho solicitar meu ingresso no QUADRO SOCIAL da UNIÃO BRASILEIRA DE COMPOSITORES,
                  na categoria de efetivo, declarando, neste ato, que tenho conhecimento do <a href='https://www.ubc.org.br/ubc/estatuto' target='_blank'>Estatuto</a> e do <a href='https://www.ubc.org.br/ubc/regimento_interno' target='_blank'>
                    Regimento Interno da associação
                  </a>, em especial ao § 5º do Art. 5º e ao § único do Art. 9º, comprometendo-me a respeitá-las.
                </span>}
              />
            </Grid>

            <Grid item>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData?.impressao || checks?.declaracaoDois || props.finalizado}
                    onChange={handleCheck}
                    name="declaracaoDois"
                  />
                }
                label={<span style={{ color: !formData?.impressao && !checks?.declaracaoDois && !props.finalizado ? '#d32f2f' : '' }}>
                  Li e concordo com a <a href='https://www.ubc.org.br/privacidade/portal' target='_blank'>
                    política de privacidade
                  </a> e o <a href='https://www.ubc.org.br/privacidade/termos' target='_blank'>
                    termo de uso
                  </a>.
                </span>}
              />
            </Grid>

            <Grid item>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData?.impressao || checks?.declaracaoTres || props.finalizado}
                    onChange={handleCheck}
                    name="declaracaoTres"
                  />
                }
                label={<span style={{ color: !formData?.impressao && !checks.declaracaoTres && !props.finalizado ? '#d32f2f' : '' }}>
                  <b>Do Uso e Armazenamento dos Dados:</b> Li e concordo expressamente com o tratamento de meus dados pessoais pela <b>UBC</b>, nos termos da Lei Geral de Proteção de Dados Pessoais (Lei nº 13.709/2018 - LGPD), compreendendo o uso, coleta, armazenamento, processamento, compartilhamento, atualização e eliminação, sempre em conformidade com as finalidades <b>descritas nos termos de privacidade e consentimento</b>.
                </span>}
              />
            </Grid>

            {props.finalizado &&
              <Grid style={{ margin: '50px 0px' }} item xs={12}>
                Rio de Janeiro, {moment(new Date()).format('DD [de] MMMM [de] YYYY')}
              </Grid>
            }
          </Grid>
        </form>
      </Box>

      <Dialog
        open={(!formData?.impressao && !props.finalizado) && dialogDocAviso}
        onClose={() => setDialogDocAviso(false)}
        aria-labelledby="dialog-titulo"
      >
        <DialogTitle id="dialog-titulo">Prezado(a) usuário(a),</DialogTitle>
        <DialogContent dividers>
          <Typography gutterBottom>
            Para documentos que exigem frente e verso (ou seja, informações em ambos os lados),
            solicitamos que você envie o arquivo completo, incluindo todas as faces com dados visíveis na imagem.
            Por exemplo, ao enviar a imagem de um RG, certifique-se de que tanto a frente quanto o verso estejam incluídos.
          </Typography>

        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogDocAviso(false)} color="primary">
            Ok
          </Button>
        </DialogActions>
      </Dialog>

    </>
  );
});

export default FormularioDocumentos;
